import React, { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import TextareaAutosize from "react-textarea-autosize";
import { Plus, X } from "react-feather";

import { ReactComponent as EmojiIcon } from "../../../../assets/img/svg/lol.svg";
import { ReactComponent as SentIcon } from "../../../../assets/img/svg/sent.svg";

import CommandBar from "../../../commandBar/CommandBar";
import EmojiPicker from "../../../emojisPicker/EmojisPicker";

import { InternalFilePreview, InternalFileThumbnail } from "./InternalFilePreview";
import InternalUploadFiles from "./InternalUploadFiles";

import "./internal-attachments.scss";

/** Tela de pré-visualização dos anexos antes do envio: preview grande, tira de
 *  miniaturas, campo de mensagem e envio. Fica sobreposta ao corpo da conversa.
 */
const InternalPreviewFilesSender = ({ files, onChangeFiles, onClose, onSend, onErrors }) => {
    const intl = useIntl();

    const inputTextRef = useRef(null);
    const [current, setCurrent] = useState(0);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const handleAddFiles = useCallback((newFiles) => {
        onChangeFiles(prevFiles => [...prevFiles, ...newFiles]);
    }, [onChangeFiles]);

    const handleRemoveFile = useCallback((index) => {
        const nextFiles = files.filter((_, i) => i !== index);

        /** Ao remover um item antes (ou o próprio) do selecionado, o índice atual
         *  passaria a apontar para outro arquivo — ou para fora da lista.
         */
        setCurrent(prevCurrent => (prevCurrent >= index ? Math.max(prevCurrent - 1, 0) : prevCurrent));

        onChangeFiles(nextFiles);
        if (nextFiles.length === 0) onClose();
    }, [files, onChangeFiles, onClose]);

    const handleGetEmoji = (emoji) => {
        const inputElement = inputTextRef.current;
        if (!inputElement) return;

        const currentValue = inputElement.value;
        const caretPosition = inputElement.selectionStart;
        const newValue = currentValue.slice(0, caretPosition) + emoji + currentValue.slice(caretPosition);

        inputElement.value = newValue;
        inputElement.selectionStart = caretPosition + emoji.length;
        inputElement.selectionEnd = caretPosition + emoji.length;
        inputElement.focus();
    }

    const handleSend = () => {
        if (files.length === 0) return;
        onSend(inputTextRef.current ? inputTextRef.current.value : "");
    }

    const handleKeyPress = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    }

    return (
        <div className="internal-preview-files-ui">
            <div className="internal-preview-files-header">
                <span role="button" onClick={onClose} className="internal-preview-files-close">
                    <X size={20} />
                </span>
            </div>

            <div className="internal-preview-files-content">
                <InternalFilePreview file={files[current]} />
            </div>

            <div className="internal-preview-files-thumbnails">
                {files.map((file, index) => (
                    <InternalFileThumbnail
                        key={`${file.name}-${index}`}
                        file={file}
                        active={index === current}
                        onSelectFile={() => setCurrent(index)}
                        onRemoveFile={() => handleRemoveFile(index)}
                    />
                ))}
                <InternalUploadFiles
                    onSelectFiles={handleAddFiles}
                    onErrors={onErrors}
                    className="internal-preview-files-add"
                >
                    <Plus size={24} />
                </InternalUploadFiles>
            </div>

            <div className={`internal-preview-files-emoji ${showEmojiPicker ? "show" : ""}`}>
                <EmojiPicker handleEmoji={handleGetEmoji} show={showEmojiPicker} />
            </div>

            <div className="internal-preview-files-footer">
                <CommandBar>
                    <CommandBar.Group>
                        <CommandBar.Item
                            color="default"
                            transparentBackground
                            active={showEmojiPicker}
                            badge={null}
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            description={null}>
                            <EmojiIcon width={25} height={25} />
                        </CommandBar.Item>
                    </CommandBar.Group>
                </CommandBar>

                <TextareaAutosize
                    ref={inputTextRef}
                    autoFocus
                    className="internal-preview-files-input"
                    placeholder={intl.formatMessage({ id: "Escreva uma mensagem" })}
                    style={{ resize: "none", height: 30 }}
                    rows={1}
                    maxRows={5}
                    onKeyDown={handleKeyPress}
                />

                <CommandBar className="chat-messaging-buttons">
                    <CommandBar.Group>
                        <CommandBar.Item
                            color="default"
                            transparentBackground
                            badge={null}
                            onClick={handleSend}
                            description={null}>
                            <SentIcon width={25} height={25} />
                        </CommandBar.Item>
                    </CommandBar.Group>
                </CommandBar>
            </div>
        </div>
    );
}

export default InternalPreviewFilesSender;
